import { useTranslation } from 'react-i18next';
import LtDialog from '../LtDialog';
import { Link } from '@lemontaps/lib/types/api';
import { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { AddEditLinkDialogProps } from './types';
import { DeleteOutline } from '@mui/icons-material';
import { AddEditThemeLinkForm } from './AddEditThemeLinkForm';

/**
 * On save always produces Link type object, but the dialog can be used for Link and LinkTemplates as well
 */
export const AddEditThemeLinkDialog = ({
  linkTypes,
  onClose,
  open,
  initialValues,
  onSave,
  templates,
  loading,
  onDelete,
  hideTemplateSelector,
  genericWebsiteColor,
}: AddEditLinkDialogProps) => {
  const [values, setValues] = useState<Partial<Link>>(initialValues || {});
  const isEdit = Boolean(values.id);
  const { t } = useTranslation();

  useEffect(() => {
    if (!initialValues) return;
    setValues(initialValues);
  }, [initialValues]);

  /** need timeout to keep smooth dialog close/open animation */
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (!open) {
      timeoutId = setTimeout(() => setValues({}), 500);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  return (
    <LtDialog
      open={open}
      onClose={onClose}
      onCancel={onClose}
      saveAction={{
        text: t('save'),
        onClick: () => onSave(values),
        disabled: !values.linkTypeId || !values.link || !values.name,
        loading,
      }}
      title={isEdit ? t('editLink') : t('createLink')}
      size='sm'
      withActionDivider
    >
      <AddEditThemeLinkForm
        linkTypes={linkTypes}
        values={values}
        onChange={setValues}
        templates={templates}
        hideTemplateSelector={hideTemplateSelector}
        genericWebsiteColor={genericWebsiteColor}
      />

      <Box mt={2} display='flex'>
        {onDelete && isEdit && (
          <Button
            sx={{ width: 'max-content', ml: 'auto' }}
            variant='text'
            startIcon={<DeleteOutline />}
            onClick={() => onDelete(values.id as number)}
          >
            {t('deleteLink')}
          </Button>
        )}
      </Box>
    </LtDialog>
  );
};
